import type { QueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import type { LiksDocumentPreviewItem } from '../../../components/liks/LiksDocumentPreviewModal/LiksDocumentPreviewModal.types';
import { knowledgeService } from '../../../services/KnowledgeService';
import { knowledgeQueryKeys } from '../knowledgeQueryKeys';

const PREFETCH_WINDOW = 3;

type UseDocumentPreviewPrefetchOptions = {
  isOpen: boolean;
  hasMultipleItems: boolean;
  activeIndex: number;
  knowledgeBaseId: string;
  resolvedItems: LiksDocumentPreviewItem[];
  queryClient: QueryClient;
};

/**
 * Prefetch nearby document metadata + download blobs for carousel preview.
 * Lives in features/knowledge so Liks UI does not call services directly.
 */
export function useDocumentPreviewPrefetch({
  isOpen,
  hasMultipleItems,
  activeIndex,
  knowledgeBaseId,
  resolvedItems,
  queryClient,
}: UseDocumentPreviewPrefetchOptions): void {
  useEffect(() => {
    if (!isOpen || !hasMultipleItems) {
      return;
    }

    const start = Math.max(0, activeIndex - PREFETCH_WINDOW);
    const end = Math.min(
      resolvedItems.length - 1,
      activeIndex + PREFETCH_WINDOW,
    );
    const resolvedKnowledgeBaseId = knowledgeBaseId.trim();

    if (resolvedKnowledgeBaseId.length === 0) {
      return;
    }

    for (let index = start; index <= end; index += 1) {
      const item = resolvedItems[index];
      if (!item?.isReadyForPreview) {
        continue;
      }

      const resolvedDocumentId = item.id.trim();
      if (resolvedDocumentId.length === 0) {
        continue;
      }

      void queryClient.prefetchQuery({
        queryKey: knowledgeQueryKeys.document(
          resolvedKnowledgeBaseId,
          resolvedDocumentId,
        ),
        queryFn: () =>
          knowledgeService.getDocument(
            resolvedKnowledgeBaseId,
            resolvedDocumentId,
          ),
      });

      void queryClient.prefetchQuery({
        queryKey: knowledgeQueryKeys.documentDownload(
          resolvedKnowledgeBaseId,
          resolvedDocumentId,
        ),
        queryFn: () =>
          knowledgeService.downloadDocument(
            resolvedKnowledgeBaseId,
            resolvedDocumentId,
          ),
        staleTime: Infinity,
      });
    }
  }, [
    activeIndex,
    hasMultipleItems,
    isOpen,
    knowledgeBaseId,
    queryClient,
    resolvedItems,
  ]);
}
